import React, { useState, useMemo, useEffect, useRef } from 'react'
import { StyleSheet, View, TouchableOpacity, FlatList, Image, ActivityIndicator } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { BottomModalBase, SearchBar } from '../molecules'
import { Text } from '../atoms/text'
import { Icon } from '../atoms/Icon'
import { Theme } from '@theme'
import { GroupedItem, SelectionItem } from '@utils'
import { AddCategoryModal } from '../../screens/contacts/addGhostContact/components/AddCategoryModal'
import { AddHashtagModal } from '../../screens/contacts/addGhostContact/components/AddHashtagModal'
import { AddButton } from '../molecules/AddButton'
import { Button } from '../molecules/Button'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import { useScreen } from '@hooks'

type ListItem = 
  | { type: 'group'; group: GroupedItem }
  | { type: 'item'; item: SelectionItem; indent?: boolean }

type GenericSelectionModalProps = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  title: string
  placeholder: string
  availableItems: SelectionItem[] | GroupedItem[]
  selectedItems: SelectionItem[]
  onSelectionChange: (items: SelectionItem[]) => void
  onClosed?: () => void
  multiSelect?: boolean
  isCategoriesModal?: boolean
  isHashtagsModal?: boolean
  fitParent?: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  fetchNextPage?: () => void
  onSearch?: (search: string) => void
}

export const GenericSelectionModal: React.FC<GenericSelectionModalProps> = ({
  isVisible,
  setIsVisible,
  title,
  placeholder,
  availableItems,
  selectedItems = [],
  onSelectionChange,
  onClosed,
  multiSelect = true,
  isCategoriesModal = false,
  isHashtagsModal = false,
  fitParent = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetching = false,
  fetchNextPage,
  onSearch,
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const [searchText, setSearchText] = useState('')
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  // Desktop, filling its parent: BottomModalBase already adds its own « Valider ».
  const hasOwnValidate = fitParent && !isMobile

  // État local pour l'affichage immédiat dans le modal
  const [localSelectedItems, setLocalSelectedItems] = useState<SelectionItem[]>(selectedItems)
  const [isAddCategoryModalVisible, setIsAddCategoryModalVisible] = useState(false)
  const [isAddHashtagModalVisible, setIsAddHashtagModalVisible] = useState(false)

  // Synchroniser l'état local avec les items sélectionnés quand le modal s'ouvre.
  // On suit les identifiants, pas le tableau lui-même : un parent qui en passe un
  // neuf à chaque rendu (ou l'absence de valeur, remplacée par un `[]` neuf)
  // relançait cet effet sans fin, ce qui figeait l'appli sur un écran noir.
  const selectedKey = selectedItems.map((item) => item.id).join('|')
  const selectedItemsRef = useRef(selectedItems)
  selectedItemsRef.current = selectedItems
  useEffect(() => {
    if (isVisible) {
      setLocalSelectedItems(selectedItemsRef.current)
    }
  }, [isVisible, selectedKey])

  // Detect if grouped
  const isGrouped = Array.isArray(availableItems) && availableItems[0] && 'items' in availableItems[0]

  // Helper to filter items by search
  const filterItems = (items: SelectionItem[]) =>
    onSearch ? items : items.filter(item => (item.name || '').toLowerCase().includes(searchText.toLowerCase()))

  // Prepare flat list data
  const flatListData = useMemo(() => {
    const data: ListItem[] = []
    
    if (isGrouped) {
      const selectedIds: Record<string, true> = {}
      localSelectedItems.forEach((item) => {
        selectedIds[item.id] = true
      })

      const groups = availableItems as GroupedItem[]
      groups.forEach((group) => {
        const filteredGroupItems = filterItems(group.items)
        if (filteredGroupItems.length > 0) {
          // IMPORTANT: keep group header adjacent to its items.
          // Only sort items *within* the group; don't sort the whole flattened list,
          // otherwise items can end up under the wrong group header.
          const sortedGroupItems = [...filteredGroupItems].sort((a, b) => {
            const aSelected = !!selectedIds[a.id]
            const bSelected = !!selectedIds[b.id]
            if (aSelected === bSelected) return 0
            return aSelected ? -1 : 1
          })

          // Add group header
          data.push({ type: 'group', group })
          // Add items from this group
          sortedGroupItems.forEach((item) => {
            data.push({ type: 'item', item, indent: true })
          })
        }
      })
      return data
    }

    const filtered = filterItems(availableItems as SelectionItem[])
    filtered.forEach((item) => {
      data.push({ type: 'item', item })
    })

    // Sort items: selected first (only meaningful for non-grouped lists)
    const selectedIds: Record<string, true> = {}
    localSelectedItems.forEach((item) => {
      selectedIds[item.id] = true
    })
    return data.sort((a, b) => {
      const aSelected = !!selectedIds[(a as any).item.id]
      const bSelected = !!selectedIds[(b as any).item.id]
      if (aSelected === bSelected) return 0
      return aSelected ? -1 : 1
    })
  }, [availableItems, searchText, localSelectedItems, isGrouped])

  // Render item for FlatList
  const renderListItem = ({ item: listItem }: { item: ListItem }) => {
    if (listItem.type === 'group') {
      return (
        <View>
          <View style={styles.row}>
            {!!listItem.group.icon && <View style={styles.icon}><Icon name={listItem.group.icon}/></View>}
            <Text type="bodyBold" style={{ marginVertical: 8 }}>{listItem.group.name}</Text>
          </View>
          {!!listItem.group.description && <Text type="bodyExtraSmall" style={{ marginBottom: 8 }}>{listItem.group.description}</Text>}
        </View>
      )
    }
    
    const item = listItem.item
    const isSelected = localSelectedItems.some(selectedItem => selectedItem.id === item.id)
    const isLastItem = flatListData[flatListData.length - 1] === listItem
    
    return (
      <TouchableOpacity
        onPress={() => toggleSelection(item.id)}
        style={item.color && item.icon? [styles.itemWithIcon, {borderColor: item.color}] : [
          styles.item,
          listItem.indent && { marginLeft: 24 },
          isLastItem && styles.lastItem,
        ]}
      >
        <View style={styles.itemContainer}>
          <View style={styles.itemContent}>
            {'photo' in item && (
              <Image source={item.photo ? { uri: item.photo } : defaultImage} style={styles.itemPhoto} />
            )}
            {item.color && !item.icon && <View style={[styles.itemPhoto, {width: 25, height: 25, borderRadius: 13, borderWidth: 1, borderColor: 'rgba(0, 0, 0, 0.12)', backgroundColor: item.color}]} />}
            {item.icon && <View style={styles.icon}><Icon name={item.icon}/></View>}
            <Text
              type={item.color && item.icon? "bodyBold" : "body"}
              color={item.color && item.icon? item.color : theme.colors.secondary}
              style={styles.itemName}
            >
              {item.name}
            </Text>
          </View>
          {!!item.text1 && <Text type="bodyExtraSmall" style={styles.itemSubtitle}>{item.text1}</Text>}
          {!!item.text2 && <Text type="bodyExtraSmall" style={styles.itemSubtitle}>{item.text2}</Text>}
        </View>
        {/* Checkbox */}
        <View style={[
          styles.checkbox, 
          isSelected && styles.checkboxSelected,
          multiSelect === false && {
            borderRadius: 20,
          }
        ]}>
          {isSelected && (
            <Icon 
              name="check" 
              size={14} 
              color={theme.colors.white} 
            />
          )}
        </View>
        {/* Selection bar */}
        {(!item.icon || !item.color) && <View style={[
          styles.selectionBar,
          isSelected && styles.selectionBarActive
        ]}/>}
      </TouchableOpacity>
    )
  }

  const toggleSelection = (itemId: string) => {
    // Flatten all items if grouped
    let allItems: SelectionItem[] = []
    if (isGrouped) {
      (availableItems as GroupedItem[]).forEach(group => {
        allItems = allItems.concat(group.items)
      })
    } else {
      allItems = availableItems as SelectionItem[]
    }
    
    const item = allItems.find(i => i.id === itemId)
    if (!item) return

    let newSelection: SelectionItem[]
    if (multiSelect) {
      const isCurrentlySelected = localSelectedItems.some(selectedItem => selectedItem.id === itemId)
      if (isCurrentlySelected) {
        newSelection = localSelectedItems.filter(selectedItem => selectedItem.id !== itemId)
      } else {
        newSelection = [...localSelectedItems, item]
      }
    } else {
      // Mode sélection unique
      const isCurrentlySelected = localSelectedItems.some(selectedItem => selectedItem.id === itemId)
      newSelection = isCurrentlySelected ? [] : [item]
    }

    // Mettre à jour l'état local immédiatement pour l'affichage
    setLocalSelectedItems(newSelection)
    // Notifier le parent
    onSelectionChange(newSelection)
  }

  const handleClose = () => {
    setIsVisible(false)
    setSearchText('')
    onSearch?.('')
    onClosed?.()
  }

  const selectedCount = localSelectedItems.length

  return (
    <BottomModalBase isVisible={isVisible} setIsVisible={setIsVisible} onClose={handleClose} largePaddingTop={false} fitParent={fitParent} validateTitle={t('common.save')}>
      <View style={styles.container}>
        {/* Header avec bouton fermer */}
        <View style={styles.header}>
          <TouchableOpacity 
            onPress={handleClose}
            style={styles.closeButton}
          >
            <Icon name="cross" size={30} color={theme.colors.secondary} />
          </TouchableOpacity>
        </View>

        {/* Titre */}
        <View style={styles.titleContainer}>
          <Text type="titleBold" style={styles.title}>
            {title}
          </Text>
          {(isCategoriesModal || isHashtagsModal) && (
            <AddButton onPress={() => {
              if (isCategoriesModal) {
                setIsAddCategoryModalVisible(true)
              } else {
                setIsAddHashtagModalVisible(true)
              }
            }} />
          )}
        </View>
        
        {/* Barre de recherche */}
        <SearchBar
          onChangeText={(text) => {
            setSearchText(text)
            onSearch?.(text)
          }}
          value={searchText}
          placeholder={placeholder}
        />

        {/* Compteur de sélection */}
        {multiSelect && (
          <Text type="body" style={styles.selectionCount}>
            {t('common.selectedCount', { count: selectedCount })}
          </Text>
        )}
        
        {/* Liste des items */}
        <FlatList
          data={flatListData}
          renderItem={renderListItem}
          keyExtractor={(item, index) => 
            item.type === 'group' 
              ? `group-${item.group.id}-${index}` 
              : `item-${item.item.id}-${index}`
          }
          style={styles.itemsList}
          showsVerticalScrollIndicator={false}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
              fetchNextPage()
            }
          }}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isFetching || isFetchingNextPage ? (
              <ActivityIndicator size="large" />
            ) : null
          }
        />
        {/* The choices are already applied as they are ticked: this validates
            them and closes, instead of having to find the X or swipe back. */}
        {!hasOwnValidate && <View style={styles.footer}>
          <Button
            title={t('common.save')}
            onPress={handleClose}
            isSmall
            style={styles.saveButton}
            testID="selection-modal-save"
          />
        </View>}
      </View>
      <AddCategoryModal
        isVisible={isAddCategoryModalVisible}
        setIsVisible={setIsAddCategoryModalVisible}
      />
      <AddHashtagModal
        isVisible={isAddHashtagModalVisible}
        setIsVisible={setIsAddHashtagModalVisible}
      />
    </BottomModalBase>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing.sm,
    },
    closeButton: {
      position: 'absolute',
      top: -theme.spacing.xxl/2,
      right: 0,
      padding: theme.spacing.xs,
    },
    titleContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: theme.spacing.xxxl,
      gap: theme.spacing.lg,
    },
    title: {
      textAlign: 'center',
      color: theme.colors.secondary,
    },
    addCategoryButton: {
      borderRadius: 20,
      backgroundColor: theme.colors.primary + 20,
      padding: theme.spacing.xs,
    },
    selectionCount: {
      textAlign: 'left',
      marginTop: theme.spacing.lg,
      color: theme.colors.greyBlue,
      fontSize: theme.fontSize.small,
    },
    itemsList: {
      width: '100%',
      marginTop: theme.spacing.lg,
    },
    item: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.lightGrey,
    },
    itemWithIcon: {
      borderWidth: 1,
      borderRadius: theme.spacing.xxxl,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      marginBottom: theme.spacing.md
    },
    lastItem: {
      borderBottomWidth: 0,
    },
    itemContainer: {
      flex: 1,
      marginRight: theme.spacing.md
    },
    itemContent: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    itemSubtitle: {
      marginTop: theme.spacing.sm
    },
    flag: {
      fontSize: theme.fontSize.large,
      marginRight: theme.spacing.sm,
    },
    itemPhoto: {
      width: 50,
      height: 50,
      borderRadius: 25,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: theme.spacing.lg,
    },
    icon: {
      marginRight: theme.spacing.lg,
    },
    itemName: {
      flex: 1,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderWidth: 2,
      borderColor: theme.colors.lightGrey,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.white,
      borderRadius: 20,
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    selectionBar: {
      height: 40,
      width: 6,
      borderRadius: 3,
      backgroundColor: theme.colors.white,
      marginLeft: theme.spacing.md,
    },
    selectionBarActive: {
      backgroundColor: theme.colors.primary,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center'
    },
    footer: {
      alignItems: 'center',
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey10,
      backgroundColor: theme.colors.white,
    },
    saveButton: {
      minWidth: 220,
      paddingHorizontal: theme.spacing.xxxl,
    }
  }) 